import React, { useState } from "react";
import axiosInstance from "../axiosinstance";
import "./Forms.css";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert

export default function ServiceMasterForm({ isSidebarExpanded }) {
  const [formData, setFormData] = useState({
    serviceName: "",
    purchaseCost: "",
    sellingCost: "",
    gst: "",
  });

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({
    serviceName: "",
    purchaseCost: "",
    sellingCost: "",
    gst: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // success or error
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    setFormErrors({
      ...formErrors,
      [name]: "",
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.serviceName) {
      errors.serviceName = "Service Name is required.";
    }

    if (!formData.sellingCost) {
      errors.sellingCost = "Selling Cost is required.";
    } else if (isNaN(formData.sellingCost) || formData.sellingCost <= 0) {
      errors.sellingCost = "Selling Cost must be a positive number.";
    }

    if (!formData.gst) {
      errors.gst = "GST is required.";
    } else if (isNaN(formData.gst) || formData.gst < 0 || formData.gst > 100) {
      errors.gst = "GST must be a number between 0 and 100.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // If validation fails, stop the submission
    }

    const serviceData = {
      service_name: formData.serviceName,
      purchase_cost: formData.purchaseCost,
      selling_cost: formData.sellingCost,
      gst_rate: formData.gst,
    };

    try {
      const response = await axiosInstance.post(
        `/api/create_service`,
        serviceData
      );
      // Handle success (e.g., display a success message)
      setSnackbarMessage("Service Master created successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setError(null); // Clear any previous error
      setTimeout(() => {
        navigate("/service-master");
      }, 2000);
    } catch (err) {
      // Handle error from API
      setSnackbarMessage(err.response?.data.message || "Something went wrong.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setSuccessMessage(null); // Clear any previous success message
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCancel = () => {
    window.history.back();
  };

  return (
    <div
      className={`table-container service-master-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body service-master-form-content">
        <div className="service-master-page-heading">
          <h1 className="form-title jost-bold">New Service Master</h1>
          <p className="service-master-page-subtitle">
            Create a service with its pricing and applicable GST details.
          </p>
        </div>
        <div className="service-master-form-card">
        <div className="form-body service-master-form-body">
          <form onSubmit={handleSubmit} className="form-box service-master-form-box">
            <div className="form-body-container service-master-form-sections">
              <section className="service-master-section service-master-section-last">
                <div className="service-master-section-header">
                  <div>
                    <h2 className="service-master-section-title">
                      Service Details
                    </h2>
                    <p className="service-master-section-description">
                      Add pricing and GST information for this service.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label jost-regular">
                  Service Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Service Name"
                  name="serviceName"
                  value={formData.serviceName}
                  onChange={handleChange}
                  autoFocus
                />
                {formErrors.serviceName && (
                  <div className="error-message">{formErrors.serviceName}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">Purchase Cost</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Purchase Cost"
                  name="purchaseCost"
                  value={formData.purchaseCost}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">
                  Selling Cost <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter Selling Cost"
                  name="sellingCost"
                  value={formData.sellingCost}
                  onChange={handleChange}
                />
                {formErrors.sellingCost && (
                  <div className="error-message">{formErrors.sellingCost}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label jost-regular">
                  GST in % <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Enter GST in %"
                  name="gst"
                  value={formData.gst}
                  onChange={handleChange}
                />
                {formErrors.gst && (
                  <div className="error-message">{formErrors.gst}</div>
                )}
              </div>

              {/* Display success or error message */}
            </section>
            </div>
          </form>
        </div>
        <div className="form-footer">
          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleCancel}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="btn btn-save"
              onClick={handleSubmit}
            >
              SAVE
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* Snackbar to display success or error message */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
